import { z } from "zod";
import { clinicalPhaseSchema } from "./clinical-phase";
import {
  overviewCompletionRequestSchema,
  type OverviewCompletionRequest,
} from "./active-medical-case";
import { completionItemSchema } from "./completion-picker";

// TES-978 — the monthly review's decisions beside the renewal itself.
//
// Emelie: the monthly review uses the same review, documentation and decision
// structure as the first care meeting and the prescription transfer, and in
// that one workspace Kirill must be able to
//   "fatta dos-, recept-, kompletterings- och uppföljningsbeslut".
// Dose and prescription were already there (approve / adjust / correct).
// This file adds the other two: the komplettering (the TES-950 picker's
// selection, see completion-picker.ts) and "Nästa uppföljning".

// ---- "Nästa uppföljning" ----------------------------------------------------

// HOW the next follow-up happens. Built from what the product already has: the
// monthly check-in that every cycle ends with (the default), a care meeting
// (booked as today), or something else the doctor names in the note.
//
// ASSUMPTION, pending Emelie's words: the three labels below are neutral
// wording of ours, not hers.
export const NEXT_FOLLOW_UP_KINDS = [
  "monthly_checkin",
  "care_meeting",
  "other",
] as const;
export const nextFollowUpKindSchema = z.enum(NEXT_FOLLOW_UP_KINDS);
export type NextFollowUpKind = z.infer<typeof nextFollowUpKindSchema>;

export const NEXT_FOLLOW_UP_KIND_LABEL: Record<NextFollowUpKind, string> = {
  monthly_checkin: "Månadsincheckning",
  care_meeting: "Vårdmöte",
  other: "Annan uppföljning",
};

export const NEXT_FOLLOW_UP_NOTE_MAX = 1000;

// A calendar day, YYYY-MM-DD, that exists.
const calendarDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((s) => {
    const d = new Date(`${s}T00:00:00.000Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
  }, "invalid_date");

// PUT …/cycles/:cycleId/next-follow-up. WHEN is always a day: a follow-up
// without a date is not a decision anyone can act on. "Annan uppföljning"
// also needs the note, since the kind alone says nothing about how.
export const setNextFollowUpSchema = z
  .object({
    kind: nextFollowUpKindSchema,
    on: calendarDaySchema,
    note: z.string().trim().max(NEXT_FOLLOW_UP_NOTE_MAX).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.kind === "other" && !v.note) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["note"],
        message: "note_required_for_other",
      });
    }
  });
export type SetNextFollowUp = z.infer<typeof setNextFollowUpSchema>;

export const nextFollowUpSchema = z.object({
  kind: nextFollowUpKindSchema,
  on: z.string(),
  note: z.string().nullable(),
  setAt: z.string(),
  setByEmployeeId: z.string().uuid().nullable(),
  setByDisplayName: z.string().nullable(),
});
export type NextFollowUp = z.infer<typeof nextFollowUpSchema>;

/**
 * The decision as one line of the journal note: it leads "Nästa uppföljning"
 * when the note is assembled (MONTHLY_JOURNAL_LAYOUT.followUpSection).
 */
export function nextFollowUpLine(
  d: Pick<NextFollowUp, "kind" | "on" | "note">,
): string {
  const head = `${NEXT_FOLLOW_UP_KIND_LABEL[d.kind]} ${d.on}`;
  const note = d.note?.trim();
  return note ? `${head}. ${note}` : head;
}

// ---- the komplettering ------------------------------------------------------

// POST …/cycles/:cycleId/completion — the TES-950 picker's selection, exactly
// as the transfer review and the lab review send it (no outcome: the monthly
// review asks the outcome with its own buttons).
//
// TES-1216 — and, like the overview's request (TES-1181), the remiss tick
// when it asks for a new blood test. The request reaches her checklist at
// once, so the later check at publish came too late:
// "Patienten får inte uppmanas att lämna blodprov innan Kirill har bekräftat att provbeställningen/remissen faktiskt är skapad i Webdoc."
export const requestCycleCompletionSchema = overviewCompletionRequestSchema;
export type RequestCycleCompletion = OverviewCompletionRequest;

export const cycleCompletionSchema = z.object({
  requestedAt: z.string(),
  items: z.array(completionItemSchema),
  // TES-1058 — the heading of each "Annan specificerad komplettering" asked
  // for on this review, so the journal names it (journal-decision.ts).
  // Optional so a response from before the field existed still parses.
  otherHeadings: z.array(z.string()).optional(),
});
export type CycleCompletion = z.infer<typeof cycleCompletionSchema>;

/**
 * TES-1034 (Emelie, point 2) — the Webdoc step's remiss row
 * (WEBDOC_LAB_ORDER_LABEL) shows only when the review's komplettering asked
 * for a new blood test, and the API then requires it on every decision in the
 * review. The same rule as the care meeting's (documentMeetingSchema): the
 * picker decides the need; the row only confirms the order is placed.
 */
export function monthlyReviewNeedsLabOrder(
  completion: Pick<CycleCompletion, "items"> | null | undefined,
): boolean {
  return completion?.items.includes("new_blood_test") ?? false;
}

// GET …/cycles/:cycleId/decisions — what the review has decided so far.
export const monthlyReviewDecisionsSchema = z.object({
  nextFollowUp: nextFollowUpSchema.nullable(),
  // The day the next monthly check-in opens if the renewal is decided today:
  // the default the follow-up field starts from.
  suggestedFollowUpOn: z.string(),
  completion: cycleCompletionSchema.nullable(),
  // TES-984 (Ö-268) — the patient's clinical phase, shown READ-ONLY: a monthly
  // renewal chooses no phase, and changing it is a separate doctor action.
  // Null when no doctor has chosen one (never defaulted, TES-841).
  clinicalPhase: clinicalPhaseSchema.nullable(),
  // TES-1034 — the free-text "Klinisk anteckning" written on this cycle before
  // the field was removed (Emelie: everything Kirill writes goes under its
  // journal heading). Read-only history; null when none was written.
  // Optional so a response from before the field existed still parses.
  legacyClinicalNote: z.string().nullable().optional(),
});
export type MonthlyReviewDecisions = z.infer<
  typeof monthlyReviewDecisionsSchema
>;
